import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  FaBitcoin, 
  FaEthereum, 
  FaLock, 
  FaEnvelope, 
  FaUser, 
  FaMobile, 
  FaArrowRight, 
  FaShieldAlt,
  FaInfoCircle
} from 'react-icons/fa';
import { SiRipple, SiLitecoin, SiDogecoin, SiTether } from 'react-icons/si';
import RegisterLoading from './loaders/RegisterLoading';

const SignUp = () => {
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: '',
    role: 'USER'
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const navigate = useNavigate();
  const [invitationCode, setInvitationCode] = useState('');
  const location = useLocation();

  // Styles
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#0F172A',
      backgroundImage: 'radial-gradient(circle at 10% 20%, rgba(37, 99, 235, 0.1) 0%, transparent 20%), radial-gradient(circle at 90% 80%, rgba(37, 99, 235, 0.1) 0%, transparent 20%)',
      padding: '20px',
      fontFamily: 'Inter, system-ui, sans-serif',
    },
    logoContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '20px',
    },
    logo: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#F8FAFC',
      textShadow: '0 0 10px rgba(56, 189, 248, 0.4)',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    logoIcon: {
      fontSize: '28px',
      color: '#38BDF8',
    },
    card: {
      backgroundColor: '#1E293B',
      borderRadius: '16px',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.25)',
      width: '100%',
      maxWidth: '400px',
      overflow: 'hidden',
      border: '1px solid #334155',
    },
    cardHeader: {
      padding: '24px',
      borderBottom: '1px solid #334155',
      textAlign: 'center',
    },
    title: {
      fontSize: '22px',
      fontWeight: '700',
      color: '#F8FAFC',
      marginBottom: '8px',
    },
    subtitle: {
      fontSize: '14px',
      color: '#94A3B8',
    },
    cryptoIcons: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '12px',
      marginTop: '16px',
    },
    cryptoIcon: {
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '18px',
    },
    bitcoinIcon: {
      backgroundColor: 'rgba(247, 147, 26, 0.1)',
      color: '#F7931A',
    },
    ethereumIcon: {
      backgroundColor: 'rgba(98, 126, 234, 0.1)',
      color: '#627EEA',
    },
    rippleIcon: {
      backgroundColor: 'rgba(0, 102, 177, 0.1)',
      color: '#0066B1',
    },
    litecoinIcon: {
      backgroundColor: 'rgba(181, 181, 181, 0.1)',
      color: '#B5B5B5',
    },
    dogeIcon: {
      backgroundColor: 'rgba(195, 166, 52, 0.1)',
      color: '#C3A634',
    },
    tetherIcon: {
      backgroundColor: 'rgba(38, 161, 123, 0.1)',
      color: '#26A17B',
    },
    form: {
      padding: '24px',
    },
    inputGroup: {
      marginBottom: '20px',
      position: 'relative',
    },
    inputLabel: {
      position: 'absolute',
      top: '50%',
      left: '16px',
      transform: 'translateY(-50%)',
      color: '#64748B',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      pointerEvents: 'none',
    },
    input: {
      width: '100%',
      backgroundColor: '#0F172A',
      borderRadius: '8px',
      border: '1px solid #334155',
      padding: '14px 16px 14px 42px',
      fontSize: '16px',
      color: '#F8FAFC',
      transition: 'border-color 0.2s ease',
    },
    inputFocus: {
      borderColor: '#38BDF8',
      outline: 'none',
      boxShadow: '0 0 0 2px rgba(56, 189, 248, 0.2)',
    },
    error: {
      color: '#EF4444',
      fontSize: '12px',
      marginTop: '6px',
      paddingLeft: '2px',
    },
    button: {
      width: '100%',
      backgroundColor: '#2563EB',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '14px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      transition: 'background-color 0.2s ease',
      position: 'relative',
      overflow: 'hidden',
    },
    buttonHover: {
      backgroundColor: '#1D4ED8',
    },
    buttonShine: {
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
      animation: 'shine 1.5s infinite',
    },
    message: {
      textAlign: 'center',
      padding: '10px',
      borderRadius: '8px',
      marginBottom: '20px',
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    successMessage: {
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      color: '#10B981',
      border: '1px solid rgba(16, 185, 129, 0.2)',
    },
    errorMessage: {
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      color: '#EF4444',
      border: '1px solid rgba(239, 68, 68, 0.2)',
    },
    loginLink: {
      textAlign: 'center',
      marginTop: '24px',
      marginBottom: '16px',
      fontSize: '14px',
      color: '#94A3B8',
    },
    link: {
      color: '#38BDF8',
      textDecoration: 'none',
      fontWeight: '500',
    },
    infoBox: {
      backgroundColor: 'rgba(56, 189, 248, 0.1)',
      borderRadius: '8px',
      padding: '12px',
      marginTop: '16px',
      display: 'flex',
      gap: '10px',
      alignItems: 'flex-start',
    },
    infoIcon: {
      color: '#38BDF8',
      fontSize: '16px',
      marginTop: '2px',
    },
    infoText: {
      fontSize: '12px',
      color: '#94A3B8',
      lineHeight: 1.5,
    },
    inviteBox: {
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      borderRadius: '8px',
      padding: '12px',
      marginBottom: '20px',
      display: 'flex',
      gap: '10px',
      alignItems: 'center',
    },
    inviteIcon: {
      color: '#10B981',
      fontSize: '16px',
    },
    inviteText: {
      fontSize: '13px',
      color: '#10B981',
    },
    helpButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      backgroundColor: '#1E293B',
      border: '1px solid #334155',
      borderRadius: '8px',
      padding: '12px',
      fontSize: '14px',
      color: '#94A3B8',
      cursor: 'pointer',
      marginTop: '16px',
      width: '100%',
      transition: 'all 0.2s ease',
    },
    helpButtonHover: {
      backgroundColor: '#0F172A',
      color: '#F8FAFC',
    },
    loaderContainer: {
      display: 'flex',
      justifyContent: 'center',
      marginBottom: '16px',
    },
  };

  useEffect(() => {
    // Add Google Tag tracking script
    const script = document.createElement('script');
    script.async = true;
    script.src = "https://www.googletagmanager.com/gtag/js?id=G-EYB77V4G2N";
    document.head.appendChild(script);

    const gtagScript = document.createElement('script');
    gtagScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-EYB77V4G2N');
    `;
    document.head.appendChild(gtagScript);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('invite_code');
    if (code) {
      setInvitationCode(code);
    }
  }, [location]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const validate = () => {
    const errors = {};
    if (!form.fullName.trim()) errors.fullName = 'Full Name is required';
    if (!form.email) {
      errors.email = 'Email Address is required';
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      errors.email = 'Email Address is invalid';
    }
    if (!form.mobile) {
      errors.mobile = 'Mobile Number is required';
    } else if (!/^(07|01|254)\d+$/.test(form.mobile)) {
      errors.mobile = 'Phone number must start with 07, 01, or 254';
    }
    if (!form.password) {
      errors.password = 'Password is required';
    } else if (form.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    if (!form.confirmPassword) {
      errors.confirmPassword = 'Confirm Password is required';
    } else if (form.confirmPassword !== form.password) {
      errors.confirmPassword = 'Passwords do not match';
    }
    return errors;
  };

  const whatsappservice = () => {
    const whatsappLink = "https://t.me/Novasoftwave";
    window.open(whatsappLink, '_blank');
  };

  const trackSignupConversion = () => {
    // Track signup event in Google Analytics
    if (window.gtag) {
      window.gtag('event', 'conversion_event_signup', {
        'event_category': 'User',
        'event_label': 'Registration',
        'value': 1
      });

      // Track specific conversion for Google Ads
      window.gtag('event', 'conversion', {
        'send_to': 'AW-306546856/rGbQCKmHjtQZEKiRlpIB',
        'event_callback': () => {
          console.log('Conversion tracked successfully');
        }
      });

      // Push to dataLayer for GTM
      window.dataLayer.push({
        'event': 'signup_complete',
        'user_type': invitationCode ? 'invited' : 'organic',
        'signup_method': 'email'
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setMessage('');
    setMessageType('');

    try {
      const response = await axios.post('https://api.novasoftwaves.com/register', form, {
        params: { invitationCode },
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 200) {
        // Track successful signup
        trackSignupConversion();
        
        setMessage('Registration successful! Please login.');
        setMessageType('success');
        navigate('/portfolio', { state: { email: form.email } });
      }
    } catch (error) {
      if (error.response?.data?.error) {
        setMessage(error.response.data.error);
      } else {
        setMessage('An error occurred. Please try again.');
      }
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      {/* GTM noscript iframe */}
      <noscript>
        <iframe 
          src="https://www.googletagmanager.com/ns.html?id=GTM-T6TXCVCW"
          height="0" 
          width="0" 
          style={{ display: 'none', visibility: 'hidden' }}
          title="Google Tag Manager"
        />
      </noscript>
      
      <div style={styles.logoContainer}>
        <h1 style={styles.logo}>
          <FaBitcoin style={styles.logoIcon} />
          CryptoNova
        </h1>
      </div>
      
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h2 style={styles.title}>Create Account</h2>
          <p style={styles.subtitle}>Join our crypto mining platform</p>
          <div style={styles.cryptoIcons}>
            <div style={{...styles.cryptoIcon, ...styles.bitcoinIcon}}>
              <FaBitcoin />
            </div>
            <div style={{...styles.cryptoIcon, ...styles.ethereumIcon}}>
              <FaEthereum />
            </div>
            <div style={{...styles.cryptoIcon, ...styles.rippleIcon}}>
              <SiRipple />
            </div>
            <div style={{...styles.cryptoIcon, ...styles.litecoinIcon}}>
              <SiLitecoin />
            </div>
            <div style={{...styles.cryptoIcon, ...styles.dogeIcon}}>
              <SiDogecoin />
            </div>
            <div style={{...styles.cryptoIcon, ...styles.tetherIcon}}>
              <SiTether />
            </div>
          </div>
        </div>
        
        {invitationCode && (
          <div style={styles.inviteBox}>
            <FaShieldAlt style={styles.inviteIcon} />
            <p style={styles.inviteText}>You've been invited! Special benefits await.</p>
          </div>
        )}
        
        {message && (
          <div style={{
            ...styles.message,
            ...(messageType === 'success' ? styles.successMessage : styles.errorMessage)
          }}>
            {message}
          </div>
        )}
        
        {loading && (
          <div style={styles.loaderContainer}>
            <RegisterLoading />
          </div>
        )}
        
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label htmlFor="fullName" style={styles.inputLabel}>
              <FaUser />
            </label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              placeholder="Full Name"
              value={form.fullName}
              onChange={handleChange}
              style={styles.input}
              onFocus={(e) => e.target.style.borderColor = '#38BDF8'}
              onBlur={(e) => e.target.style.borderColor = '#334155'}
            />
            {errors.fullName && <p style={styles.error}>{errors.fullName}</p>}
          </div>
          
          <div style={styles.inputGroup}>
            <label htmlFor="email" style={styles.inputLabel}>
              <FaEnvelope />
            </label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="Email Address"
              value={form.email}
              onChange={handleChange}
              style={styles.input}
              onFocus={(e) => e.target.style.borderColor = '#38BDF8'}
              onBlur={(e) => e.target.style.borderColor = '#334155'}
            />
            {errors.email && <p style={styles.error}>{errors.email}</p>}
          </div>
          
          <div style={styles.inputGroup}>
            <label htmlFor="mobile" style={styles.inputLabel}>
              <FaMobile />
            </label>
            <input
              type="number"
              id="mobile"
              name="mobile"
              placeholder="Phone Number"
              value={form.mobile}
              onChange={handleChange}
              style={styles.input}
              onFocus={(e) => e.target.style.borderColor = '#38BDF8'}
              onBlur={(e) => e.target.style.borderColor = '#334155'}
            />
            {errors.mobile && <p style={styles.error}>{errors.mobile}</p>}
          </div>
          
          <div style={styles.inputGroup}>
            <label htmlFor="password" style={styles.inputLabel}>
              <FaLock />
            </label>
            <input
              type="password"
              id="password"
              name="password"
              placeholder="Password"
              value={form.password}
              onChange={handleChange}
              style={styles.input}
              onFocus={(e) => e.target.style.borderColor = '#38BDF8'}
              onBlur={(e) => e.target.style.borderColor = '#334155'}
            />
            {errors.password && <p style={styles.error}>{errors.password}</p>}
          </div>
          
          <div style={styles.inputGroup}>
            <label htmlFor="confirmPassword" style={styles.inputLabel}>
              <FaLock />
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              placeholder="Confirm Password"
              value={form.confirmPassword}
              onChange={handleChange}
              style={styles.input}
              onFocus={(e) => e.target.style.borderColor = '#38BDF8'}
              onBlur={(e) => e.target.style.borderColor = '#334155'}
            />
            {errors.confirmPassword && <p style={styles.error}>{errors.confirmPassword}</p>}
          </div>
          
          <button 
            type="submit" 
            style={styles.button}
            onMouseOver={(e) => e.target.style.backgroundColor = '#1D4ED8'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#2563EB'}
            disabled={loading}
          >
            <span>Create Your Account</span>
            <FaArrowRight />
            <span style={styles.buttonShine}></span>
          </button>
          
          <div style={styles.infoBox}>
            <FaInfoCircle style={styles.infoIcon} />
            <p style={styles.infoText}>
              By signing up, you'll get access to our crypto mining platform with daily rewards and secure withdrawals.
            </p>
          </div>
        </form>
        
        <div style={styles.loginLink}>
          Already have an account? <a href="/login" style={styles.link}>Login</a>
        </div>
        
        <button 
          style={styles.helpButton}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = '#0F172A';
            e.target.style.color = '#F8FAFC';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = '#1E293B';
            e.target.style.color = '#94A3B8';
          }}
          onClick={whatsappservice}
        >
          24/7 Helpline
        </button>
      </div>
      
      <style jsx>{`
        @keyframes shine {
          0% { left: -100%; }
          20% { left: 100%; }
          100% { left: 100%; }
        }
      `}</style>
    </div>
  );
};

export default SignUp;